'use client';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

function IndividualSaintContent() {
  const searchParams = useSearchParams();
  const saintId = searchParams?.get('saint') || 'st_francis';

  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
  }, []);

  // Mock saint data (would come from database/API)
  const saint = {
    id: saintId,
    name: 'St. Francis of Assisi',
    title: 'Patron of Animals and Ecology',
    feastDay: 'October 4',
    born: '1181',
    died: '1226',
    canonized: '1228',
    era: 'Middle Ages (1181-1226)',
    region: 'Assisi, Italy',
    patronOf: ['Animals', 'Ecology', 'Merchants', 'Italy', 'Environment', 'Zoos', 'Pets'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_117aab0ae-1765689274966.png",
    alt: 'St. Francis of Assisi in brown robes surrounded by birds in forest',
    biography: `St. Francis of Assisi, born Giovanni di Pietro di Bernardone in 1181, was the son of a wealthy cloth merchant in Assisi, Italy. As a young man, he lived a carefree and luxurious life, enjoying the privileges of his family's wealth.

However, after a series of spiritual experiences, including a vision of Christ and hearing a voice from the crucifix at San Damiano, Francis underwent a profound conversion. He renounced his wealth, stripped himself of his fine clothes in the public square, and dedicated his life to serving God through radical poverty.

Francis founded the Franciscan Order in 1209, which emphasized living in poverty, preaching the Gospel, and caring for the sick and poor. His order grew rapidly, attracting followers who were inspired by his simple yet profound spirituality.

He was known for his deep love of nature and all God's creatures. Francis preached to animals, calling them his brothers and sisters. He composed the famous "Canticle of the Sun," praising God through all creation.

In 1224, while praying on Mount La Verna, Francis received the stigmata - the wounds of Christ in his hands, feet, and side. He was the first recorded person to bear these marks. Francis died on October 3, 1226, and was canonized by Pope Gregory IX just two years later in 1228.`,
    prayer: `Most High, glorious God,
enlighten the darkness of my heart
and give me true faith, certain hope,
and perfect charity, sense and knowledge,
Lord, that I may carry out
Your holy and true command.
Amen.`,
    lifeEvents: [
    { year: '1181', event: 'Born in Assisi, Italy' },
    { year: '1202', event: 'Captured in war, imprisoned for a year' },
    { year: '1206', event: 'Heard voice from crucifix: "Francis, repair my church"' },
    { year: '1209', event: 'Founded Franciscan Order' },
    { year: '1212', event: 'Co-founded Order of Poor Ladies with St. Clare' },
    { year: '1224', event: 'Received the stigmata on Mount La Verna' },
    { year: '1226', event: 'Died on October 3 in Assisi' },
    { year: '1228', event: 'Canonized by Pope Gregory IX' }]

  };

  const products = [
  {
    id: 'prod_1',
    name: 'St. Francis Medal - Sterling Silver',
    price: 24.99,
    image: "https://images.unsplash.com/photo-1701638026668-05e8ebd8230d",
    alt: 'Sterling silver St. Francis medal with chain',
    description: 'Beautiful sterling silver medal with detailed engraving'
  },
  {
    id: 'prod_2',
    name: 'St. Francis Prayer Card Set (10 pack)',
    price: 9.99,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17abdd994-1770044974315.png",
    alt: 'Set of laminated St. Francis prayer cards',
    description: 'Laminated prayer cards with Prayer of St. Francis'
  },
  {
    id: 'prod_3',
    name: 'St. Francis Statue - 8 inch Resin',
    price: 34.99,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_117aab0ae-1765689274966.png",
    alt: 'Resin statue of St. Francis with birds',
    description: 'Hand-painted resin statue with birds and animals'
  },
  {
    id: 'prod_4',
    name: 'St. Francis Rosary Beads - Olive Wood',
    price: 19.99,
    image: "https://images.unsplash.com/photo-1558419841-b74c7d4a903e",
    alt: 'Wooden rosary beads with St. Francis centerpiece',
    description: 'Handcrafted from Holy Land olive wood'
  },
  {
    id: 'prod_5',
    name: 'St. Francis Wall Art Print - 11x14',
    price: 29.99,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_117aab0ae-1765689274966.png",
    alt: 'Framed art print of St. Francis with animals',
    description: 'Museum-quality print on archival paper'
  },
  {
    id: 'prod_6',
    name: 'St. Francis Socks - Novelty Gift',
    price: 12.99,
    image: "https://images.unsplash.com/photo-1733743247160-9baed388d0f6",
    alt: 'Colorful socks with St. Francis and animal designs',
    description: 'Comfortable cotton blend with fun designs'
  },
  {
    id: 'prod_7',
    name: 'St. Francis Keychain - Bronze',
    price: 7.99,
    image: "https://images.unsplash.com/photo-1729381864920-1fe98775b13b",
    alt: 'Bronze keychain with St. Francis medal',
    description: 'Durable bronze medal keychain'
  },
  {
    id: 'prod_8',
    name: 'St. Francis Candle - Beeswax',
    price: 16.99,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11cd68d1d-1765988479041.png",
    alt: 'Natural beeswax candle with St. Francis image',
    description: '100% pure beeswax with natural honey scent'
  },
  {
    id: 'prod_9',
    name: 'St. Francis Book - Biography',
    price: 18.99,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a797d8c2-1770044974796.png",
    alt: 'Hardcover biography of St. Francis',
    description: 'Comprehensive biography with historical context'
  },
  {
    id: 'prod_10',
    name: 'St. Francis Garden Statue - 18 inch',
    price: 89.99,
    image: "https://images.unsplash.com/photo-1687848749345-938de598bc53",
    alt: 'Large outdoor garden statue of St. Francis',
    description: 'Weather-resistant resin for outdoor use'
  },
  {
    id: 'prod_11',
    name: 'St. Francis Bracelet - Leather',
    price: 14.99,
    image: "https://images.unsplash.com/photo-1560193586-066e0b81af91",
    alt: 'Brown leather bracelet with St. Francis charm',
    description: 'Adjustable leather bracelet with metal charm'
  },
  {
    id: 'prod_12',
    name: 'St. Francis Coffee Mug',
    price: 13.99,
    image: "https://images.unsplash.com/photo-1594867785489-0a3f55e779a1",
    alt: 'Ceramic mug with St. Francis quote and image',
    description: 'Dishwasher safe ceramic mug, 11oz capacity'
  }];


  const relatedSaints = [
  {
    id: 'st_clare',
    name: 'St. Clare of Assisi',
    feastDay: 'August 11',
    patronOf: 'Television',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae0e8d46-1764918833965.png",
    alt: 'St. Clare of Assisi in prayer'
  },
  {
    id: 'st_anthony',
    name: 'St. Anthony of Padua',
    feastDay: 'June 13',
    patronOf: 'Lost Items',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1432c75e7-1765689274474.png",
    alt: 'St. Anthony of Padua holding infant Jesus'
  },
  {
    id: 'st_bonaventure',
    name: 'St. Bonaventure',
    feastDay: 'July 15',
    patronOf: 'Theologians',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a90afc4c-1764689638855.png",
    alt: 'St. Bonaventure in Franciscan habit writing'
  },
  {
    id: 'st_elizabeth_hungary',
    name: 'St. Elizabeth of Hungary',
    feastDay: 'November 17',
    patronOf: 'Bakers',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a80248a9-1771014807606.png",
    alt: 'St. Elizabeth of Hungary giving bread to poor'
  }];


  if (!isHydrated) {
    return null;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative h-[500px] overflow-hidden">
          <AppImage
            src={saint?.image}
            alt={saint?.alt}
            className="absolute inset-0 w-full h-full object-cover" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          
          <div className="relative h-full flex items-end">
            <div className="max-w-7xl mx-auto px-6 pb-12 w-full">
              <Link
                href="/search-saints"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
                
                <Icon name="ArrowLeftIcon" size={16} />
                <span className="text-sm">Back to Search</span>
              </Link>

              <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-3">
                {saint?.name}
              </h1>
              <p className="text-2xl text-white/90">{saint?.title}</p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Biography */}
              <div className="reveal">
                <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                  Life & Biography
                </h2>
                <div className="prose prose-lg max-w-none">
                  {saint?.biography?.split('\n\n')?.map((paragraph, index) =>
                  <p key={index} className="text-muted-foreground leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  )}
                </div>
              </div>

              {/* Life Timeline */}
              <div className="reveal">
                <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                  Life Timeline
                </h2>
                <div className="space-y-4">
                  {saint?.lifeEvents?.map((event, index) =>
                  <div key={index} className="flex gap-6 group">
                      <div className="flex-shrink-0 w-20 text-right">
                        <span className="text-lg font-bold text-primary">{event?.year}</span>
                      </div>
                      <div className="flex-shrink-0 w-px bg-border relative">
                        <div className="absolute top-2 -left-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-white group-hover:scale-125 transition-transform" />
                      </div>
                      <div className="flex-1 pb-8">
                        <p className="text-foreground font-medium">{event?.event}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Prayer */}
              <div className="glass-card p-8 rounded-2xl border-2 border-primary/20 reveal">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Icon name="HandRaisedIcon" size={24} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-serif font-bold text-foreground">
                    Prayer to {saint?.name}
                  </h2>
                </div>
                <blockquote className="text-lg font-serif italic text-foreground leading-relaxed whitespace-pre-line mb-6">
                  {saint?.prayer}
                </blockquote>
                <button className="flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all">
                  <Icon name="ArrowDownTrayIcon" size={20} />
                  <span>Download Prayer Card</span>
                </button>
              </div>
            </div>

            {/* Right Column - Info Sidebar */}
            <div className="space-y-6">
              <div className="glass-card p-6 rounded-2xl border border-border sticky top-32 reveal">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Saint Information
                </h3>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Feast Day</dt>
                    <dd className="text-foreground font-medium flex items-center gap-2">
                      <Icon name="CalendarIcon" size={16} className="text-primary" />
                      {saint?.feastDay}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Born</dt>
                    <dd className="text-foreground font-medium">{saint?.born}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Died</dt>
                    <dd className="text-foreground font-medium">{saint?.died}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Canonized</dt>
                    <dd className="text-foreground font-medium">{saint?.canonized}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Era</dt>
                    <dd className="text-foreground font-medium">{saint?.era}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-1">Region</dt>
                    <dd className="text-foreground font-medium flex items-center gap-2">
                      <Icon name="MapPinIcon" size={16} className="text-primary" />
                      {saint?.region}
                    </dd>
                  </div>
                </dl>

                <div className="mt-6 pt-6 border-t border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Patron Of</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {saint?.patronOf?.map((patron) =>
                    <span
                      key={patron}
                      className="text-xs bg-primary/10 text-primary px-2.5 py-1.5 rounded-full font-medium">
                      
                        {patron}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-border space-y-3">
                  <button className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors">
                    <Icon name="ShareIcon" size={20} />
                    <span>Share this Saint</span>
                  </button>
                  <Link
                    href="/search-saints"
                    className="w-full flex items-center justify-center gap-2 border-2 border-border px-4 py-3 rounded-xl font-medium hover:border-primary hover:text-primary transition-colors">
                    
                    <Icon name="MagnifyingGlassIcon" size={20} />
                    <span>Search More Saints</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Shop Section */}
          <div className="mt-24 reveal">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-foreground mb-4">
                Shop {saint?.name} Merchandise
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Honor and remember {saint?.name} with these meaningful items
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products?.map((product) =>
              <div
                key={product?.id}
                className="glass-card rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border group">
                
                  <div className="aspect-square overflow-hidden">
                    <AppImage
                    src={product?.image}
                    alt={product?.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">
                      {product?.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                      {product?.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary">
                        ${product?.price}
                      </span>
                      <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1">
                        <Icon name="ShoppingCartIcon" size={16} />
                        <span>Add</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Related Saints */}
          <div className="mt-24 reveal">
            <h2 className="text-3xl font-serif font-bold text-foreground mb-8">
              Related Saints
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedSaints?.map((relatedSaint) =>
              <Link
                key={relatedSaint?.id}
                href={`/individual-saint?saint=${relatedSaint?.id}`}
                className="group glass-card rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border">
                
                  <div className="aspect-[3/4] overflow-hidden">
                    <AppImage
                    src={relatedSaint?.image}
                    alt={relatedSaint?.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif font-semibold text-foreground mb-1 text-sm">
                      {relatedSaint?.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      {relatedSaint?.feastDay}
                    </p>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                      {relatedSaint?.patronOf}
                    </span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>);


}

export default function IndividualSaintPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-primary">Loading...</div></div>}>
      <IndividualSaintContent />
    </Suspense>);

}